import type { ComponentProps } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { t } from "ttag";

import { createWeightEntry } from "@/app/api/legacy-api";
import { LogoLoader, UnitSystemSwitch } from "@/shared/components";
import { WeightEntryInput } from "@/shared/domains";
import { formatDate } from "@/shared/lib";
import {
  MAX_ENTRY_NOTE_CHARACTERS,
  UNIT_SYSTEM,
  WEIGHT_UNIT,
} from "@/shared/models";
import { NOW } from "@/shared/models/legacy-constants";
import { useSettingsStore } from "@/shared/stores";
import {
  Button,
  CalendarMinimalisticIcon,
  Dialog,
  errorToast,
  Form,
  GraphIcon,
  Input,
  LegacyDatePicker,
  TextArea,
  useToastStore,
} from "@/shared/ui";
import { convertKgToPounds, handleAxiosFieldErrors } from "@/shared/utils";

export const CreateWeightEntry = ({
  onOpenChange,
  ...props
}: ComponentProps<typeof Dialog.Root>) => {
  const { pushToast } = useToastStore();
  const unitSystem = useSettingsStore((state) => state.unitSystem);

  const form = useForm({
    resolver: zodResolver(WeightEntryInput),
    defaultValues: {
      unitSystem,
      date: formatDate(NOW, "yyyy-MM-dd"),
    },
  });

  const queryClient = useQueryClient();
  const { mutate: createWeightEntryMutation, isPending } = useMutation({
    mutationFn: createWeightEntry.mutation,
    onSuccess: () => {
      onOpenChange?.(false);
      createWeightEntry.invalidates(queryClient);
      void pushToast({
        type: "success",
        title: t`Success`,
        message: t`Weight entry successfully created!`,
      });
      form.reset();
    },
    onError: (err) => {
      errorToast(err);
      handleAxiosFieldErrors(err, form.setError);
    },
  });

  const handleSubmit = form.handleSubmit((values) => {
    const { weight, ...rest } = values;

    const weightInLbs =
      unitSystem === UNIT_SYSTEM.METRIC ? convertKgToPounds(weight) : weight;

    createWeightEntryMutation({
      weight: weightInLbs,
      ...rest,
    });
  });

  const uploadDisabled = !form.formState.isDirty || isPending;

  return (
    <Dialog.Root onOpenChange={onOpenChange} {...props}>
      <Dialog.Content>
        <Dialog.Header icon={GraphIcon}>
          <Dialog.Title>{t`Log entry`}</Dialog.Title>
          <Dialog.Description>
            {t`Ready to track your progress? Share your current weight with us below! Let's work towards your goals together!`}
          </Dialog.Description>
        </Dialog.Header>

        <Form.Provider {...form}>
          <form onSubmit={handleSubmit} id="create-entry-form" className="grow">
            <UnitSystemSwitch className="ml-auto flex" />

            <Form.Field
              control={form.control}
              name="date"
              render={({ field }) => (
                <Form.Item>
                  <Form.Label>{t`Date *`}</Form.Label>
                  <Form.Control>
                    <LegacyDatePicker
                      config={{ dates: { maxDate: NOW } }}
                      left={<CalendarMinimalisticIcon />}
                      {...field}
                    />
                  </Form.Control>
                  <Form.Message compact={false} />
                </Form.Item>
              )}
            />

            <Form.Field
              control={form.control}
              name="weight"
              render={() => (
                <Form.Item>
                  <Form.Label>{t`Weight (${WEIGHT_UNIT[unitSystem]}) *`}</Form.Label>
                  <Form.Control>
                    <Input.Root
                      placeholder={t`Your weight`}
                      type="number"
                      step=".01"
                      {...form.register("weight", { setValueAs: Number })}
                    />
                  </Form.Control>
                  <Form.Message compact={false} />
                </Form.Item>
              )}
            />

            <Form.Field
              control={form.control}
              name="note"
              render={({ field }) => (
                <Form.Item>
                  <Form.Label>{t`Note (optional)`}</Form.Label>
                  <Form.Control>
                    <TextArea
                      placeholder={t`Optional note`}
                      rows={4}
                      {...field}
                    />
                  </Form.Control>
                  <div className="-mt-2 flex h-fit justify-between gap-2">
                    <Form.Message compact={false} className="mt-0" />
                    <Form.CharCounter max={MAX_ENTRY_NOTE_CHARACTERS} />
                  </div>
                </Form.Item>
              )}
            />
          </form>
        </Form.Provider>

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button disabled={isPending} variant="secondary" size="lg">
              {t`Cancel`}
            </Button>
          </Dialog.Close>

          <Button
            disabled={uploadDisabled}
            type="submit"
            form="create-entry-form"
            size="lg"
          >
            {t`Upload`}
          </Button>
        </Dialog.Footer>

        <LogoLoader show={isPending} fullscreen />
      </Dialog.Content>
    </Dialog.Root>
  );
};
